import { useCallback, useEffect, useState } from "react";
import {
  listSkills, getSkill, upsertSkill, testSkill,
  type SkillDescriptor, type SkillDetail, type SkillTestResponse,
} from "../api";
import { CodeCard, GridLoader, Pill } from "./ai/Primitives";
import { IconExternal, IconSkills } from "./icons";
import { buildCanonicalPipeline } from "../pipeline";

const MINI_GLYPH: Record<string, string> = {
  input: "⇪", extract: "⊟", agent: "◈", template: "▤", render: "⚙", output: "✓",
};


/**
 * Skills view (Phase C / gap 4). Lists an instance's skills (discovered via the
 * executor), shows each skill's YAML + schema, runs a test with a JSON input and
 * shows the trace, and edits the YAML — which opens a `skill/<inst>-` PR that
 * flows through the SAME Phase-A review gate as synapse changes.
 *
 * Edits + test-runs need a write role (owner/writer) — the session is the
 * credential, enforced by the Worker like every other write action.
 */

export function SkillsPanel({
  open, instance, onClose, onOpenPipeline,
}: {
  open: boolean;
  instance: string;
  onClose: () => void;
  onOpenPipeline?: () => void;
}) {
  const [skills, setSkills] = useState<SkillDescriptor[]>([]);
  const [executorReady, setExecutorReady] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<SkillDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // edit + test sub-state (scoped to the selected skill)
  const [editMode, setEditMode] = useState(false);
  const [editYaml, setEditYaml] = useState("");
  const [busy, setBusy] = useState(false);
  const [prInfo, setPrInfo] = useState<{ pr_number: number; pr_url: string } | null>(null);
  const [testInput, setTestInput] = useState("{}");
  const [testResult, setTestResult] = useState<SkillTestResponse | null>(null);
  const [testing, setTesting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await listSkills(instance);
      setSkills(r.skills);
      setExecutorReady(r.executor_ready);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);


  const openDetail = async (id: string) => {
    setSelected(id); setDetail(null); setEditMode(false);
    setTestResult(null); setTestInput("{}"); setPrInfo(null); setErr(null);
    try {
      const d = await getSkill(instance, id);
      setDetail(d);
      setEditYaml(d.yaml);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const onSaveEdit = async () => {
    if (!selected) return;
    setBusy(true); setErr(null); setPrInfo(null);
    try {
      const r = await upsertSkill(instance, selected, editYaml,
        `skill(${instance}): edit ${selected} via web`);
      setPrInfo({ pr_number: r.pr_number, pr_url: r.pr_url });
      setEditMode(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onTest = async () => {
    if (!selected) return;
    let parsed: Record<string, unknown>;
    try { parsed = JSON.parse(testInput || "{}"); }
    catch { setErr("Test input is not valid JSON."); return; }
    setTesting(true); setErr(null); setTestResult(null);
    try {
      setTestResult(await testSkill(instance, selected, parsed));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setTesting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
        <h2 className="modal-title">Skills — <code>{instance}</code></h2>
        <p className="modal-hint">
          Inspect, test, and edit skills. Edits open a reviewable PR (same gate as
          synapse changes) — nothing changes live until you approve it in
          <strong> Changes</strong>.
        </p>

        {!executorReady && (
          <div className="error-banner">
            Skill executor not configured (no <code>SKILL_EXECUTOR_URL</code>). Listing
            + test-runs are unavailable until a skill-server is wired up.
          </div>
        )}


        {err && <div className="error-banner">{err}</div>}

        <div className="skills-layout">
          {/* left: skill list */}
          <div className="skills-list">
            {loading && <GridLoader label="Loading skills" />}
            {!loading && skills.length === 0 && (
              <div className="skills-empty">No skills for this instance yet.</div>
            )}
            {skills.map((s) => (
              <button
                key={s.id}
                className={`skills-item${selected === s.id ? " active" : ""}`}
                onClick={() => openDetail(s.id)}
              >
                <div className="skills-item-id">{s.id}</div>
                <div className="skills-item-desc">{s.description || "No description."}</div>
                {s.version && <Pill tone="neutral">v{s.version}</Pill>}
              </button>
            ))}
          </div>

          {/* right: detail */}
          <div className="skills-detail">
            {!selected && (
              <div className="empty-state">
                <span className="empty-mark"><IconSkills size={22} /></span>
                <h2>Pick a skill</h2>
                <p className="empty-sub">Inspect its definition, run it against test input, or edit it into a reviewable PR.</p>
              </div>
            )}

            {selected && (
              <>
                <div className="skills-detail-head">
                  <code className="skills-detail-id">{selected}</code>
                  <div className="skills-detail-actions">
                    {!editMode && detail && (
                      <button onClick={() => { setEditMode(true); setPrInfo(null); }}>Edit YAML</button>
                    )}
                    {editMode && (
                      <>
                        <button className="primary" disabled={busy} onClick={onSaveEdit}>
                          {busy ? "Saving…" : "Save to PR"}
                        </button>
                        <button disabled={busy} onClick={() => { setEditMode(false); setEditYaml(detail?.yaml || ""); }}>
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {prInfo && (
                  <div className="skills-pr-notice">
                    <Pill tone="done" dot>Staged</Pill>
                    <span>
                      PR #{prInfo.pr_number} opened — approve it in <strong>Changes</strong>.
                    </span>
                    <a href={prInfo.pr_url} target="_blank" rel="noreferrer">
                      GitHub <IconExternal size={11} />
                    </a>
                  </div>
                )}

                {!detail && <GridLoader label="Loading definition" />}

                {detail && !editMode && (
                  <>
                    <div className="skills-pipeline-mini">
                      <div className="skills-pipeline-mini-head">
                        <span className="skills-pipeline-mini-label">Document pipeline</span>
                        {onOpenPipeline && (
                          <button className="skills-pipeline-link" onClick={onOpenPipeline}>View full graph</button>
                        )}
                      </div>
                      <div className="pipeline-chain-mini">
                        {buildCanonicalPipeline(selected!, instance).nodes
                          .filter(n => n.kind !== "template")
                          .map((n, i, arr) => (
                            <span key={n.id} className="pipeline-mini-wrap">
                              <span className={`pipeline-node-mini kind-${n.kind}`}>
                                {MINI_GLYPH[n.kind]} {n.kind === "agent" ? "skill" : n.kind}
                              </span>
                              {i < arr.length - 1 && <span className="pipeline-arrow">→</span>}
                            </span>
                          ))}
                        <span className="pipeline-mini-feed">+ template</span>
                      </div>
                    </div>
                    <CodeCard name={`${selected}.yaml`} lang="YAML" code={detail.yaml}>
                      <pre className="skills-yaml">{detail.yaml}</pre>
                    </CodeCard>
                  </>
                )}
                {detail && editMode && (
                  <textarea
                    className="skills-yaml-edit"
                    value={editYaml}
                    onChange={e => setEditYaml(e.target.value)}
                    spellCheck={false}
                  />
                )}

                {/* test runner */}
                <div className="skills-test">
                  <div className="skills-test-head">Test run</div>
                  <textarea
                    className="skills-test-input"
                    value={testInput}
                    onChange={e => setTestInput(e.target.value)}
                    placeholder='{"key": "value"}'
                    spellCheck={false}
                  />
                  <button className="primary" disabled={testing || !executorReady} onClick={onTest}>
                    {testing ? "Running…" : "Run test"}
                  </button>
                  {testResult && (
                    <div className={`skills-test-result ${testResult.ok ? "ok" : "fail"}`}>
                      {!testResult.ok && (
                        <div className="error-banner">
                          {testResult.error || "The skill run failed."}
                          {testResult.hint && <div className="modal-hint">{testResult.hint}</div>}
                        </div>
                      )}
                      {testResult.ok && (
                        <>
                          {testResult.warnings && testResult.warnings.length > 0 && (
                            <div className="skills-warn">
                              <Pill tone="running">Warning</Pill>
                              <span>{testResult.warnings.join("; ")}</span>
                            </div>
                          )}
                          {testResult.tool_calls && testResult.tool_calls.length > 0 && (
                            <div className="skills-trace">
                              <div className="skills-trace-head">Tool calls</div>
                              {testResult.tool_calls.map((t, i) => (
                                <div key={i} className="skills-trace-call">
                                  <code>{t.name}</code>
                                  {t.result_summary && <span> — {t.result_summary}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                          {testResult.cited_synapses && testResult.cited_synapses.length > 0 && (
                            <div className="skills-cited">
                              <span className="skills-cited-label">Cited</span>
                              {testResult.cited_synapses.map(c => <code key={c}>{c}</code>)}
                            </div>
                          )}
                          {testResult.attachments && testResult.attachments.length > 0 && (
                            <div className="skills-cited">
                              <span className="skills-cited-label">Attachments</span>
                              {testResult.attachments.map(a => (
                                <code key={a.name}>{a.name} ({a.bytes}B)</code>
                              ))}
                            </div>
                          )}
                          <pre className="skills-payload">
                            {JSON.stringify(testResult.payload, null, 2)}
                          </pre>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="modal-actions">
          <button onClick={onClose}>Close</button>
          <button onClick={refresh} disabled={loading}>Refresh</button>
        </div>
      </div>

    </div>
  );
}
